import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Drawer from "../Drawer";
import { LoaderSpinner } from "../LoaderSpinner";
import { BudgetLoadingKeys, clearBudgetHistory, getBudgetHistory } from "../../slices/quickbooks/budgetSlice";
import { useDebounce } from "../../helpers/useDebounce";

const MONTH_LABELS = {
    jan: "January",
    feb: "February",
    mar: "March",
    apr: "April",
    may: "May",
    jun: "June",
    jul: "July",
    aug: "August",
    sep: "September",
    oct: "October",
    nov: "November",
    dec: "December",
};

const TYPE_LABELS = {
    EDITABLE: "Editable",
    CALCULATED: "Calculated",
    FORECAST_VALUE: "Forecast Value",
};

const formatChangedAt = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

const formatType = (value) => TYPE_LABELS[String(value || "").trim()] || value || "—";

const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    return String(value);
};

const getHistoryRows = (items) => (items || []).flatMap((item) => {
    const account = `${item.accountNumber || ""} ${item.accountName || ""}`.trim() || "—";
    const rows = (item.monthChanges || []).map((change) => ({
        id: `${item.id}-${change.month}`,
        changedAt: item.changedAt,
        account,
        field: MONTH_LABELS[change.month] || String(change.month || "").toUpperCase(),
        oldValue: change.oldFormatted,
        newValue: change.newFormatted,
        userName: item.userName,
    }));

    if (item.typeFrom !== item.typeTo && (item.typeFrom || item.typeTo)) {
        rows.push({
            id: `${item.id}-type`,
            changedAt: item.changedAt,
            account,
            field: "Type",
            oldValue: formatType(item.typeFrom),
            newValue: formatType(item.typeTo),
            userName: item.userName,
        });
    }

    if ((item.changedFields || []).some((field) => field === "roll_up_items")) {
        rows.push({
            id: `${item.id}-rollup`,
            changedAt: item.changedAt,
            account,
            field: "Rollup Items",
            oldValue: "—",
            newValue: (item.rollupItemNames || []).join(", ") || "None",
            userName: item.userName,
        });
    }

    return rows;
});

export const BudgetHistorySidebar = ({
    isOpen,
    budgetId,
    onClose,
}) => {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const debouncedSearchTerm = useDebounce(searchTerm, 400);
    const {
        loadingKeys,
        budgetHistory,
        budgetHistoryTotal,
        budgetHistoryPageSize,
    } = useSelector((state) => state.budget);
    const loading = loadingKeys.includes(BudgetLoadingKeys.getBudgetHistory);

    useEffect(() => {
        if (!isOpen || !budgetId) return;
        dispatch(getBudgetHistory({
            budgetId,
            search: debouncedSearchTerm.trim(),
            page: currentPage,
            pageSize: 25,
            replace: true,
        }));
    }, [budgetId, currentPage, debouncedSearchTerm, dispatch, isOpen]);

    useEffect(() => {
        if (isOpen) return;
        setSearchTerm("");
        setCurrentPage(1);
        dispatch(clearBudgetHistory());
    }, [dispatch, isOpen]);

    const rows = useMemo(() => getHistoryRows(budgetHistory), [budgetHistory]);
    const pageSize = Number(budgetHistoryPageSize || 25);
    const totalPages = Math.max(1, Math.ceil(Number(budgetHistoryTotal || 0) / pageSize));
    const isLastPage = currentPage >= totalPages;

    if (!isOpen) return null;

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            width="900px"
            showActionButtons={false}
            headerContent={
                <div className="flex items-center justify-between gap-3 pr-2">
                    <h2 className="text-xl font-bold text-gray-900">Budget Change History</h2>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            disabled={currentPage <= 1 || loading}
                            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                            className="rounded-md bg-white p-1.5 text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label="Previous page"
                        >
                            <ChevronLeftIcon className="h-3 w-3" />
                        </button>
                        <p className="min-w-[3rem] text-center text-xs text-gray-500">
                            {currentPage}/{totalPages}
                        </p>
                        <button
                            type="button"
                            disabled={isLastPage || loading}
                            onClick={() => setCurrentPage((page) => page + 1)}
                            className="rounded-md bg-white p-1.5 text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label="Next page"
                        >
                            <ChevronRightIcon className="h-3 w-3" />
                        </button>
                    </div>
                </div>
            }
        >
            <div className="flex flex-col gap-3 px-4 pb-6">
                <p className="mt-4 text-sm text-gray-500">
                    Every change to a budget line item is logged here.
                </p>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => {
                        setSearchTerm(event.target.value);
                        setCurrentPage(1);
                    }}
                    placeholder="Filter by account name or number…"
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
                />
                {loading ? (
                    <div className="flex justify-center py-10">
                        <LoaderSpinner width="48" height="48" border="4" colorSpinner="gray" />
                    </div>
                ) : !rows.length ? (
                    <p className="py-10 text-center text-sm text-gray-500">No budget changes logged yet.</p>
                ) : (
                    <div className="overflow-x-auto rounded-md border border-gray-200">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-gray-50 text-gray-600">
                                <tr>
                                    {["When", "Account", "Field", "Change", "By"].map((heading) => (
                                        <th key={heading} className="whitespace-nowrap px-3 py-2 font-medium">{heading}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50/80">
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-500">{formatChangedAt(row.changedAt)}</td>
                                        <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-800">{row.account}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-700">{row.field}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                                            {formatValue(row.oldValue)} <span className="mx-1 text-gray-400">→</span> {formatValue(row.newValue)}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-500">{row.userName || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Drawer>
    );
};
